import React, { useState } from 'react';
import type { FC } from 'react';

import { Panel } from '@shared/components/bootstrap/panel';
import { Picture } from '@shared/components/helpers';
import { Lightbox } from '@shared/components/helpers/lightbox';
import { ImgixBG, ImgixFit } from '@shared/utils/imgix';

import { Titleize } from '@admin/components/helpers/titleize';
import type { InventoryPallet } from '@admin/components/inventory/types';
import { QaQrCodeLink } from '@admin/components/qa_automation/qa_qr_code_link';

const DENSITY_PALLET_TYPES = ['Single', 'Double', 'Stackable Crate'];

const parseDensityPercentage = (raw: unknown): number | undefined => {
  if (raw == null) return undefined;
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
};

const gradeLabelClass = (score: string) => {
  switch (score) {
    case 'good':
      return 'label-success';
    case 'acceptable':
      return 'label-warning';
    case 'fail':
      return 'label-danger';
    default:
      return 'label-default';
  }
};

export const InventoryPalletRow: FC<{ pallet: InventoryPallet }> = ({ pallet }) => {
  const [selection, setSelection] = useState<{ source: string } | undefined>();

  const gradeImage = pallet.grade?.image;
  const densityPercentage = parseDensityPercentage(pallet.density_metrics?.percentage);
  const showDensity = DENSITY_PALLET_TYPES.includes(pallet.type.name) && densityPercentage !== undefined;

  return (
    <>
      <Panel>
        <Panel.Body>
          <div className="pull-left">
            {gradeImage && (
              <div className="pull-left panel-pallet-image">
                <a
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (gradeImage.state !== 'uploaded') return;
                    setSelection(gradeImage);
                  }}
                >
                  <figure>
                    <Picture
                      source={gradeImage.state === 'uploaded' ? gradeImage.source : undefined}
                      width={50}
                      height={50}
                      background={ImgixBG.White}
                      fit={ImgixFit.Fill}
                      alt="Pallet grade"
                    />
                  </figure>
                </a>
              </div>
            )}
            <div className="pull-right">
              <a href={`/pallets/${pallet.id}`} className="btn-link">
                #{pallet.barcode.value}
              </a>{' '}
              <strong>({pallet.state})</strong>
              {pallet.grade?.score && (
                <>
                  {' '}
                  <span className={`label ${gradeLabelClass(pallet.grade.score)}`}>
                    <Titleize text={pallet.grade.score} />
                  </span>
                </>
              )}
              <QaQrCodeLink value={pallet.barcode.value} />
              <br />
              <span>
                {pallet.type.name} ({pallet.items.length}/{pallet.items_count}{' '}
                {pallet.items.length === 1 ? 'Item' : 'Items'})
              </span>
              {showDensity && (
                <>
                  {' '}
                  <label className={`label ${densityPercentage >= 75 ? 'label-success' : 'label-danger'}`}>
                    Density: {densityPercentage.toFixed(2)}%
                  </label>
                </>
              )}
              {pallet.size !== 'oversized' && pallet.height && (
                <>
                  {' '}
                  <label className="label label-default">Height: {pallet.height.value} in.</label>
                </>
              )}
            </div>
          </div>
          {pallet.location && (
            <div className="pull-right">
              <div>
                <strong>
                  <Titleize text={pallet.location.located.type} />:
                </strong>{' '}
                <span>{pallet.location.located.object.name}</span>
              </div>
              <div>
                <strong>Location:</strong>{' '}
                <a href={`/locations/${pallet.location.id}`} className="text-primary">
                  #{pallet.location.value}
                </a>{' '}
                <QaQrCodeLink value={pallet.location.value} />
              </div>
            </div>
          )}
        </Panel.Body>
      </Panel>

      {selection && <Lightbox imageURL={selection.source} onClose={() => setSelection(undefined)} />}
    </>
  );
};
